import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Users, Money, Handshake, ChatText, ArrowsClockwise } from "@phosphor-icons/react";

const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
};

const RANGES = [
  { key: "week",  label: "This week",  days: 7 },
  { key: "month", label: "This month", days: 30 },
  { key: "qtr",   label: "This qtr",   days: 90 },
  { key: "year",  label: "Year to date", days: 365 },
];

export default function TeamPage() {
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (rangeKey) => {
    setBusy(true);
    const days = RANGES.find(r => r.key === rangeKey)?.days || 30;
    const end = new Date();
    const start = new Date(); start.setDate(end.getDate() - days);
    try {
      const { data } = await api.get("/team/leaderboard", { params: {
        start_date: start.toISOString().slice(0, 10),
        end_date:   end.toISOString().slice(0, 10),
      }});
      setData(data);
    } catch (e) { toast.error("Failed to load team overview"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(range); }, [range]);

  const rows = useMemo(() => {
    const arr = [...((data && data.rows) || [])];
    arr.sort((a, b) => (b.closed_won_value || 0) - (a.closed_won_value || 0) || (b.open_pipeline_value || 0) - (a.open_pipeline_value || 0));
    return arr;
  }, [data]);

  const totals = useMemo(() => {
    return rows.reduce((a, r) => ({
      open_pipeline: a.open_pipeline + (r.open_pipeline_value || 0),
      closed_won: a.closed_won + (r.closed_won_value || 0),
      wins: a.wins + (r.closed_won_count || 0),
      moves: a.moves + (r.deals_moved_this_period || 0),
    }), { open_pipeline: 0, closed_won: 0, wins: 0, moves: 0 });
  }, [rows]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="team-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">Sales · team</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Team overview</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Every teammate at a glance. Click any card to drill into their pipeline, notes and activity timeline.</p>
        </div>
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" data-testid="team-page-range">
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setRange(r.key)}
                    data-testid={`team-page-range-${r.key}`}
                    className={`text-xs font-semibold px-3 py-1.5 transition-colors ${range === r.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Totals band */}
      <section className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="team-page-totals">
        <TotalTile icon={Users}     tone="slate"   label="Teammates"       value={rows.filter(r => r.user_id).length}/>
        <TotalTile icon={Money}     tone="blue"    label="Total pipeline"  value={fmtINR(totals.open_pipeline)}/>
        <TotalTile icon={Trophy}    tone="emerald" label="Team closed-won" value={fmtINR(totals.closed_won)} sub={`${totals.wins} wins`}/>
        <TotalTile icon={ArrowsClockwise} tone="rose" label="Deals moved"  value={totals.moves}/>
      </section>

      {/* Cards */}
      <section className="mt-6" data-testid="team-cards-grid">
        {busy && !data && <div className="text-xs text-slate-500 italic text-center py-10">Loading team…</div>}
        {!busy && rows.length === 0 && <div className="text-xs text-slate-500 italic text-center py-10 bg-white border border-slate-200 rounded-md">No teammate activity yet.</div>}
        {rows.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((r, i) => {
              const to = r.user_id ? `/team/${r.user_id}` : "/team/unassigned";
              const maxPipe = Math.max(1, ...rows.map(x => x.open_pipeline_value || 0));
              const barPct = Math.min(100, ((r.open_pipeline_value || 0) / maxPipe) * 100);
              return (
                <Link key={r.user_id || "unassigned"} to={to} data-testid={`team-card-${r.user_id || "unassigned"}`}
                      className="group bg-white border border-slate-200 rounded-md p-4 hover:border-slate-900 hover:shadow-md transition-all block">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-slate-900 text-white grid place-items-center text-sm font-bold shrink-0">{(r.name || "?").slice(0, 1).toUpperCase()}</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {i === 0 && r.closed_won_count > 0 && <Trophy size={13} weight="fill" className="text-amber-500 shrink-0"/>}
                        <div className="font-display font-black text-slate-900 truncate group-hover:text-blue-700">{r.name}</div>
                      </div>
                      {r.email && <div className="text-[11px] text-slate-500 truncate">{r.email}</div>}
                    </div>
                  </div>

                  {/* Sparkbar for open pipeline share */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                      <span className="label-micro">Open pipeline share</span>
                      <span className="tabular-nums font-semibold text-slate-700">{fmtINR(r.open_pipeline_value)}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600" style={{ width: `${barPct}%` }}/>
                    </div>
                  </div>

                  {/* Mini stats */}
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <MiniStat icon={Handshake} label="Open"       value={r.deals_owned}/>
                    <MiniStat icon={Trophy}    label={`Wins`}     value={r.closed_won_count} accent={r.closed_won_count > 0 ? "text-emerald-700" : ""}/>
                    <MiniStat icon={ChatText}  label="Notes"      value={r.notes_this_period}/>
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                    <span className="text-slate-500">Closed-won <span className="font-bold text-emerald-700 tabular-nums ml-1">{fmtINR(r.closed_won_value)}</span></span>
                    <span className="text-slate-400 group-hover:text-blue-600 font-semibold">Drill in →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function TotalTile({ icon: Icon, tone, label, value, sub }) {
  const tones = { slate: "bg-slate-100 text-slate-700", blue: "bg-blue-100 text-blue-700",
                  emerald: "bg-emerald-100 text-emerald-700", rose: "bg-rose-100 text-rose-700" };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`team-total-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-md grid place-items-center ${tones[tone] || tones.slate}`}><Icon size={14} weight="fill"/></div>
        <div className="label-micro">{label}</div>
      </div>
      <div className="font-display font-black text-xl text-slate-900">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, accent = "" }) {
  return (
    <div>
      <div className="flex items-center justify-center gap-1 text-[9px] text-slate-500 uppercase tracking-wider font-semibold">
        <Icon size={9} weight="fill"/>{label}
      </div>
      <div className={`font-display font-black text-base tabular-nums ${accent}`}>{value ?? 0}</div>
    </div>
  );
}
